import { Fragment, memo, useEffect, useMemo, useState } from 'react';
import { FormControlLabel, Grid, RadioGroup } from '@mui/material';
import PmiTextField from 'shell/components/shared/text-field';
import { LastStepVM, SectionVM, TextActVM } from 'civil-status/view-models/step-form';
import { FormikErrors, FormikProps, FormikTouched } from 'formik';
import { FormControl } from '@mui/material';
import { Radio } from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { AnscUpdateText } from 'shell/authorizations/civil-status';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { noop } from 'shell/utils/noop';
import { DateTime } from 'luxon';
import { KeySections } from 'civil-status/constants/keys-subjects-section';
import { useUserProfile } from 'shell/hooks/use-user-profile';

export const authsAnscText = {
  updateText: AnscUpdateText,
};

interface FormProps {
  values?: TextActVM;
  errors?: FormikErrors<TextActVM>;
  touched?: FormikTouched<TextActVM>;
  onChange: (key: keyof TextActVM, value: TextActVM[keyof TextActVM]) => void;
  callGetTextAct: (startCall?: boolean) => void;
  onSaveText: (values?: TextActVM) => void;
  disabled?: boolean;
  disabledValid?: boolean;
  isLastStepEditAct?: boolean;
  formContext: FormikProps<LastStepVM>;
}

const FormComponent = ({
  disabled = false,
  disabledValid = false,
  values,
  errors,
  touched,
  onChange,
  callGetTextAct,
  onSaveText,
  isLastStepEditAct,
  formContext,
}: FormProps): React.ReactElement => {
  const valuesAnnotation: SectionVM = useMemo(() => {
    return formContext.values?.[KeySections.keyAnnotazioneContestuale] || {};
  }, [formContext.values?.[KeySections.keyAnnotazioneContestuale]]);

  const [annotationsFormatted, setAnnotationsFormatted] = useState<string>('');
  const { userProfile } = useUserProfile();

  const modificaTestoEvento = useMemo(() => {
    return userProfile.datiLogin?.profile?.feature?.modificaTestoEvento;
  }, [userProfile.datiLogin?.profile?.feature?.modificaTestoEvento]);

  useEffect(() => {
    let annotationsFormattedData = '';

    const arrAnnotations = valuesAnnotation?.annotations?.slice().sort((a, b) => {
      const typeA = (a.typeContextualAnnotation || '').toLowerCase();
      const typeB = (b.typeContextualAnnotation || '').toLowerCase();
      if (typeA < typeB) {
        return -1;
      }
      if (typeA > typeB) {
        return 1;
      }
      return 0;
    });

    arrAnnotations?.forEach((item) => {
      const annotationsFormattedDataCurrent = `\n${
        item.dateInsertion || DateTime.now().toFormat('dd/MM/yyyy')
      } (${item.isNotCertifiable ? 'Non certificabile' : 'Certificabile'}) \n${
        item.textContextualAnnotation
      }`;

      annotationsFormattedData = annotationsFormattedData?.trim() + annotationsFormattedDataCurrent;
    });

    setAnnotationsFormatted(annotationsFormattedData);
  }, [valuesAnnotation?.annotations]);

  return (
    <Grid item container spacing={2} xs={12}>
      <Grid item xs={12}>
        <PmiTextField
          label="Testo dell'atto"
          aria-label="Testo dell'atto"
          id="text"
          name="text"
          fullWidth
          disabled={!values?.isEditText || disabled || isLastStepEditAct}
          helperText={errors?.text}
          error={touched?.text && Boolean(errors?.text)}
          type="text"
          variant="outlined"
          multiline
          minRows={5}
          value={values?.text || ''}
          onChange={(event) => {
            onChange('text', event.target.value);
          }}
        />
      </Grid>
      {modificaTestoEvento === '1' && !isLastStepEditAct ? (
        <Fragment>
          <Grid item xs={12} md={6}>
            <FormControl component="fieldset">
              <RadioGroup
                row
                aria-label="Modificare testo"
                id="isEditText"
                value={values?.isEditText || 'false'}
                onChange={() => {
                  if (values?.isEditText) {
                    // calcolare il testo quando la modifica viene bloccata, lo stato viene aggiornato nella funzione

                    // salvataggio del cambio del Radio sul DB
                    (onSaveText({ isEditText: false }) as unknown as ResponsePromise<void>).then(
                      () => {
                        callGetTextAct(true);
                      }
                    );
                  } else {
                    onChange('isEditText', !values?.isEditText);
                  }
                }}
              >
                <FormControlLabel
                  value="false"
                  control={<Radio />}
                  label="Testo generato automaticamente"
                  disabled={disabledValid}
                />
                <FormControlLabel
                  value="true"
                  control={<Radio />}
                  label="Testo editabile"
                  disabled={disabledValid}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6} display="flex" justifyContent="center">
            <PmiButton
              color="cdmBrandThird"
              variant="outlined"
              onClick={() => onSaveText(values)}
              disabled={disabled || disabledValid || isLastStepEditAct}
            >
              Salva
            </PmiButton>
          </Grid>
        </Fragment>
      ) : undefined}
      {Boolean(annotationsFormatted) ? (
        <Grid item xs={12}>
          <PmiTextField
            label="Annotazioni contestuali"
            aria-label="Annotazioni contestuali"
            id="text"
            name="text"
            fullWidth
            disabled
            type="text"
            variant="outlined"
            multiline
            minRows={5}
            value={annotationsFormatted}
            onChange={noop}
          />
        </Grid>
      ) : undefined}
    </Grid>
  );
};

FormComponent.displayName = 'Accordion Text Act Component';

const Form = memo(FormComponent);

export default Form;
